import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import moment, { Moment } from 'moment';
import AdapterMoment from '@mui/lab/AdapterMoment';

import {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import AddManualAdjustmentModal from './modals/AddManualAdjustmentModal';
import { checkUserPermission } from '../../../../../../helper';
import { useAppSelector } from '../../../../../../hooks';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../store/V2/RolesSlice';
import { getAuthState } from '../../../../../../store/AuthSlice';
import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../../../components/V2/Table/type1/useTable';
import {
  adminGetManualAdjustments,
  getManualAdjustmentsState,
  IManualAdjustment,
  resetAdminManualAdjustmentListData,
  TAdminGetManualAdjustments,
} from '../../../../../../store/V2/ManualAdjustmentsSlice';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../../../components/V2/Table/type1/TableHeadCustom';
import ManualAdjustmentTableRow from './components/ManualAdjustmentTableRow';
import TableNoData from '../../../../../../components/V2/Empty/TableNoData';
import TableLoader from '../../../../../../components/Loaders/TableLoader';
import TablePaginationActions from '../../../../../../components/V2/Table/type1/TablePaginationActions';
import UserListDropdown, {
  TUserSelectOption,
} from '../../../../../../components/Inputs/admin/UserListDropdown';
import {
  getLessonHistoryState,
  updateAdminLessonHistoryCommonFilterData,
} from '../../../../../../store/V2/LessonHistorySlice';
import {
  defaultTextFieldProps,
  TPTextField,
} from '../../../../../../components/Inputs';
import { getTenantsState } from '../../../../../../store/V2/TenantsSlice';

export type TFilterData = {
  limit: number;
  offset: number;
  sortKey: string;
  sortValue: number;
  startDate?: string;
  endDate?: string;
  tutorId?: string;
};

export type TState = {
  startDate: Moment | null;
  endDate: Moment | null;
  selectedTutor: TUserSelectOption | null;
};

const defaultListLimit = 30;
const defaultOrderBy = 'date';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};
const defaultStartDateMoment = moment();
const defaultEndDateMoment = defaultStartDateMoment.clone().add(1, 'day');

const defaultFilterData: TFilterData = {
  limit: defaultListLimit,
  offset: 0,
  sortKey: defaultOrderBy,
  sortValue: defaultOrder === 'desc' ? -1 : 1,
  startDate: defaultStartDateMoment.startOf('day').toISOString(),
  endDate: defaultEndDateMoment.endOf('day').toISOString(),
};

const initialState: TState = {
  startDate: defaultStartDateMoment.clone(),
  endDate: defaultEndDateMoment.clone(),
  selectedTutor: null,
};

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'date',
    label: 'Date',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'createdAt',
    label: 'Created At',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'tutor',
    label: 'Tutor',
    sortable: false,
    minWidth: 150,
    maxWidth: 150,
  },
  {
    id: 'amount',
    label: 'Amount',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'title',
    label: 'Title',
    sortable: true,
    minWidth: 180,
    maxWidth: 180,
  },
  {
    id: 'description',
    label: 'Description',
    sortable: true,
    minWidth: 200,
    maxWidth: 200,
  },
  {
    id: 'createdBy',
    label: 'Created By',
    sortable: false,
    minWidth: 150,
    maxWidth: 150,
  },
  {
    id: 'action',
    label: 'Action',
    sortable: false,
    align: 'right',
  },
];

const ManualAdjustmentsTab: FunctionComponent = () => {
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);
  const lessonHistoryState = useAppSelector(getLessonHistoryState);
  const manualAdjustmentsState = useAppSelector(getManualAdjustmentsState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;
  const { adminLessonHistoryCommonFilterData } = lessonHistoryState;
  const { adminManualAdjustmentListData } = manualAdjustmentsState;

  // State
  const tableData = useTable(defaultTableData);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [filterData, setFilterData] = useState({ ...defaultFilterData });
  const [state, setState] = useState<TState>({ ...initialState });
  const [
    viewManualAdjustmentModalData,
    setViewManualAdjustmentModalData,
  ] = useState<{ open: boolean; manualAdjustment?: IManualAdjustment }>({
    open: false,
  });

  let manualAdjustmentList: IManualAdjustment[] = [];
  let totalCount = 0;

  if (adminManualAdjustmentListData) {
    totalCount = adminManualAdjustmentListData.totalDocs || 0;

    if (
      adminManualAdjustmentListData.list &&
      adminManualAdjustmentListData.list.length
    ) {
      manualAdjustmentList = adminManualAdjustmentListData.list;
    }
  }

  useEffect(() => {
    dispatch(resetAdminManualAdjustmentListData());

    getInitialManualAdjustmentListData();

    return () => {
      dispatch(resetAdminManualAdjustmentListData());
    };
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const getManualAdjustmentListData = (filterDataAttributes: TFilterData) => {
    const apiData: TAdminGetManualAdjustments = {
      limit: filterDataAttributes.limit,
      offset: filterDataAttributes.offset,
    };

    if (filterDataAttributes.sortKey) {
      apiData.sortKey = filterDataAttributes.sortKey;
    }

    if (filterDataAttributes.sortValue) {
      apiData.sortValue = filterDataAttributes.sortValue;
    }

    if (filterDataAttributes.startDate) {
      apiData.startDate = filterDataAttributes.startDate;
    }

    if (filterDataAttributes.endDate) {
      apiData.endDate = filterDataAttributes.endDate;
    }

    if (filterDataAttributes.tutorId) {
      apiData.tutorId = filterDataAttributes.tutorId;
    }

    dispatch(adminGetManualAdjustments(apiData));
    setFilterData({ ...filterDataAttributes });
  };

  const getInitialManualAdjustmentListData = () => {
    const initialFilterData: TFilterData = {
      ...defaultFilterData,
    };

    const newStateData: Partial<TState> = {};

    if (adminLessonHistoryCommonFilterData) {
      if (adminLessonHistoryCommonFilterData.startDate) {
        newStateData.startDate = moment(
          adminLessonHistoryCommonFilterData.startDate
        );

        initialFilterData.startDate = moment(
          adminLessonHistoryCommonFilterData.startDate
        )
          .startOf('day')
          .toISOString();
      } else if (adminLessonHistoryCommonFilterData.startDateCleared) {
        newStateData.startDate = null;
        initialFilterData.startDate = undefined;
      }

      if (adminLessonHistoryCommonFilterData.endDate) {
        newStateData.endDate = moment(
          adminLessonHistoryCommonFilterData.endDate
        );

        initialFilterData.endDate = moment(
          adminLessonHistoryCommonFilterData.endDate
        )
          .endOf('day')
          .toISOString();
      } else if (adminLessonHistoryCommonFilterData.endDateCleared) {
        newStateData.endDate = null;
        initialFilterData.endDate = undefined;
      }

      if (adminLessonHistoryCommonFilterData.selectedTutor) {
        newStateData.selectedTutor = {
          ...adminLessonHistoryCommonFilterData.selectedTutor,
        };

        initialFilterData.tutorId =
          adminLessonHistoryCommonFilterData.selectedTutor.value;
      }
    }

    getManualAdjustmentListData({ ...initialFilterData });
    updateStateAttributes({ ...newStateData });
  };

  const handleSort = (id: string) => {
    let newSortValue = 1;

    if (filterData.sortKey === id && filterData.sortValue === 1) {
      newSortValue = -1;
    }

    getManualAdjustmentListData({
      ...filterData,
      sortValue: newSortValue,
      sortKey: id,
    });

    tableData.onSort(id);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    getManualAdjustmentListData({
      ...filterData,
      offset: page * filterData.limit,
    });

    tableData.onChangePage(event, page);
    tableData.setSelected([]);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    getManualAdjustmentListData({
      ...filterData,
      offset: 0,
      limit: parseInt(event.target.value, 10),
    });

    tableData.onChangeRowsPerPage(event);
  };

  const refreshList = () => {
    getManualAdjustmentListData({ ...filterData });
  };

  return (
    <div>
      <div style={{ padding: '20px' }}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Grid
            container
            alignItems="center"
            columnSpacing="16px"
            rowSpacing="20px"
          >
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <DatePicker
                  clearable
                  value={state.startDate}
                  label="Start Date"
                  inputFormat="YYYY-MMM-DD"
                  onChange={(date) => {
                    updateStateAttributes({
                      startDate: date,
                    });

                    getManualAdjustmentListData({
                      ...filterData,
                      startDate: date
                        ? date.clone().startOf('day').toISOString()
                        : undefined,
                    });

                    dispatch(
                      updateAdminLessonHistoryCommonFilterData({
                        startDate: date ? date.toISOString() : undefined,
                        startDateCleared: date ? undefined : true,
                      })
                    );
                  }}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...defaultTextFieldProps}
                      size="small"
                      placeholder="Start Date"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <DatePicker
                  clearable
                  value={state.endDate}
                  label="End Date"
                  inputFormat="YYYY-MMM-DD"
                  onChange={(date) => {
                    updateStateAttributes({
                      endDate: date,
                    });

                    getManualAdjustmentListData({
                      ...filterData,
                      endDate: date
                        ? date.clone().endOf('day').toISOString()
                        : undefined,
                    });

                    dispatch(
                      updateAdminLessonHistoryCommonFilterData({
                        endDate: date ? date.toISOString() : undefined,
                        endDateCleared: date ? undefined : true,
                      })
                    );
                  }}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...defaultTextFieldProps}
                      size="small"
                      placeholder="End Date"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <UserListDropdown
                  userTypes={['tutor']}
                  selectedItem={state.selectedTutor}
                  textFieldProps={{
                    label: 'Tutor',
                    placeholder: 'Filter by Tutor',
                    ...defaultTextFieldProps,
                  }}
                  handleChange={(e, value) => {
                    updateStateAttributes({
                      selectedTutor: value,
                    });

                    getManualAdjustmentListData({
                      ...filterData,
                      tutorId: value ? value.value : undefined,
                    });

                    dispatch(
                      updateAdminLessonHistoryCommonFilterData({
                        selectedTutor: value || undefined,
                      })
                    );
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <Tooltip title="Refresh List">
                <IconButton onClick={refreshList}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </div>

      {checkUserPermission({
        authUser,
        tenant: appTenant,
        permissions: {
          adminManualAdjustments: [
            USER_PERMISSION_INDIVIDUALS.ADMIN_MANUAL_ADJUSTMENTS_CREATE,
          ],
        },
      }) ? (
        <Stack direction="row" justifyContent="flex-end" mb="16px">
          <DarkButton
            variant="contained"
            color="inherit"
            sx={{ ...defaultButtonStyles }}
            onClick={() => setAddModalOpen(true)}
          >
            Add New Adjustment
          </DarkButton>
        </Stack>
      ) : null}

      <TableContainer
        sx={{ position: 'relative', maxHeight: 'calc(100vh - 250px)' }}
      >
        <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
          <TableHeadCustom
            order={tableData.order}
            orderBy={tableData.orderBy}
            rowCount={0}
            headLabels={tableHeaderLabels}
            numSelected={tableData.selected.length}
            onSort={handleSort}
          />
          <TableBody>
            {manualAdjustmentList.map((row) => {
              return (
                <ManualAdjustmentTableRow
                  manualAdjustment={row}
                  onView={() =>
                    setViewManualAdjustmentModalData({
                      open: true,
                      manualAdjustment: row,
                    })
                  }
                />
              );
            })}
            {manualAdjustmentsState.status !== 'loading' &&
            !manualAdjustmentList.length ? (
              <TableNoData title="No manual adjustments found" />
            ) : null}
            {manualAdjustmentsState.status === 'loading' &&
            !manualAdjustmentList.length ? (
              <TableLoader />
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={defaultRowsPerPageOptions}
          component="div"
          count={totalCount}
          rowsPerPage={tableData.rowsPerPage}
          page={tableData.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleRowsPerPageChange}
          ActionsComponent={TablePaginationActions}
        />

        <FormControlLabel
          label="Dense"
          sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
          control={
            <Switch
              checked={tableData.dense}
              onChange={tableData.onChangeDense}
            />
          }
        />
      </Box>

      {/* Modals */}
      {isAddModalOpen ? (
        <AddManualAdjustmentModal
          open={isAddModalOpen}
          onClose={() => setAddModalOpen(false)}
        />
      ) : null}
      {viewManualAdjustmentModalData.open &&
      viewManualAdjustmentModalData.manualAdjustment ? (
        <AddManualAdjustmentModal
          readOnly
          open={viewManualAdjustmentModalData.open}
          onClose={() => setViewManualAdjustmentModalData({ open: false })}
          manualAdjustment={viewManualAdjustmentModalData.manualAdjustment}
        />
      ) : null}
    </div>
  );
};

export default ManualAdjustmentsTab;
